import React from 'react';
import { ExternalLink, Edit, Trash2, Calendar, Clock, ArrowUp, ArrowDown } from 'lucide-react';

import { UsefulLink } from '../lib/supabase';

interface UsefulLinkCardProps {
  link: UsefulLink;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

const UsefulLinkCard: React.FC<UsefulLinkCardProps> = ({
  link,
  isAdmin,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown
}) => {
  const handleLinkClick = (e: React.MouseEvent) => {
    // Abre o link em nova aba para todos os usuários
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="relative">
      {/* Admin Actions - Outside the card */}
      {isAdmin && (
        <div className="absolute top-0 -right-12 z-10">
          <div className="flex flex-col space-y-2">
            {onMoveUp && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp();
                }}
                className="w-8 h-8 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center shadow-lg hover:bg-gray-700 hover:text-[#0FFF55] text-white transition-colors"
                title="Mover para cima"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            )}
            {onMoveDown && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown();
                }}
                className="w-8 h-8 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center shadow-lg hover:bg-gray-700 hover:text-[#0FFF55] text-white transition-colors"
                title="Mover para baixo"
              >
                <ArrowDown className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="w-8 h-8 bg-[#0FFF55] rounded-full flex items-center justify-center shadow-lg hover:bg-[#0dd149] transition-colors"
              title="Editar link"
            >
              <Edit className="w-4 h-4 text-black" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center shadow-lg hover:bg-red-700 transition-colors"
              title="Excluir link"
            >
              <Trash2 className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}

      {/* Main Card */}
      <div
        onClick={handleLinkClick}
        className="relative group cursor-pointer overflow-hidden bg-[#121316] rounded-2xl border-2 border-[#0FFF55] hover:border-[#0dd149] transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#0FFF55]/30"
      >
        {/* Background Image */}
        <div className="relative h-64 overflow-hidden">
          <div className="absolute inset-0 transition-all duration-500">
            <img
              src={link.image}
              alt={link.title}
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                if (img.dataset.fallbackApplied !== 'true') {
                  img.dataset.fallbackApplied = 'true';
                  img.src = 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&h=600';
                }
              }}
            />
          </div>
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* External Link Icon */}
          <div className="absolute top-4 right-4">
            <div className="w-10 h-10 bg-[#0FFF55] rounded-full flex items-center justify-center shadow-lg hover:bg-[#0dd149] transition-colors">
              <ExternalLink className="w-5 h-5 text-black" />
            </div>
          </div>

          {/* Title - Main Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h3 className="text-white font-bold text-2xl leading-tight group-hover:text-[#0FFF55] transition-colors mb-2">
              {link.title}
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              {link.description}
            </p>
          </div>
        </div>

        {/* Card Footer */}
        <div className="p-6 bg-[#1a1d21] border-t border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-gray-400 text-sm">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Criado em {formatDate(link.created_at)}</span>
              </div>
              {link.updated_at !== link.created_at && (
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>Atualizado em {formatDate(link.updated_at)}</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2 text-[#0FFF55]">
              <span className="text-sm font-medium">Clique para acessar</span>
              <ExternalLink className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Hover Effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0FFF55]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
      </div>
    </div>
  );
};

export default UsefulLinkCard; 